import { CAMERA_CAPTURE } from "./video/camera-capture";

/**
 * Le champ de la caméra d'un téléphone : ce que la page propose pour filmer
 * plus large, selon ce que le navigateur expose.
 *
 * # Deux chemins vers le grand angle
 *
 * Sous iOS, WebKit ouvre de préférence la caméra virtuelle de l'iPhone —
 * triple, ou double grand angle — et cale son zoom 1 sur l'objectif principal :
 * le grand angle y est le zoom 0,5, comme dans l'appareil photo. Changer de
 * zoom ne rouvre pas la caméra et ne coupe pas l'image.
 *
 * Chrome sous Android ne zoome qu'en recadrant, jamais sous 1×. Le grand angle
 * n'y existe qu'en caméra à part, quand le constructeur l'expose, et le nom que
 * donne le navigateur ne dit pas quel objectif est lequel : la page liste les
 * caméras du côté filmé, et l'aperçu montre le champ de chacune.
 *
 * Quand le zoom descend sous 1×, la page ne liste pas les caméras : sous iOS,
 * elles ne feraient que répéter ce que le zoom donne déjà, en coupant l'image
 * à chaque changement.
 */

/** Le côté d'où filme la caméra, dans les mots de `facingMode`. */
export type CameraFacing = "environment" | "user";

/** Une caméra de l'appareil, telle que le navigateur la liste. */
export interface CameraDevice {
  deviceId: string;
  label: string;
  /** Les côtés que le navigateur lui connaît : aucun quand il n'en dit rien. */
  facing: readonly string[];
}

/** Une caméra proposée au choix, sous un nom à montrer. */
export interface CameraLens {
  deviceId: string;
  label: string;
}

/** L'étendue du zoom d'une piste : `zoom` n'est pas encore dans les types du DOM. */
export interface ZoomRange {
  min?: number;
  max?: number;
}

/** Ce que la page propose pour changer de champ. */
export type LensOffer = { kind: "zoom"; wide: number } | { kind: "devices"; lenses: readonly CameraLens[] };

/** Ce qu'on a choisi pour un côté, et que la caméra rouverte doit retrouver. */
export type LensChoice = { kind: "zoom"; zoom: number } | { kind: "device"; deviceId: string };

/**
 * Sous ce zoom, la caméra filme nettement plus large que son objectif
 * principal. Un minimum à peine sous 1 ne serait qu'un arrondi : proposer
 * « 1× » à côté de « 1× » n'aurait pas de sens.
 */
const WIDE_ZOOM_BELOW = 0.95;

/** Le zoom du grand angle, quand la piste descend sous 1×. */
export function wideZoomOf(zoom: ZoomRange | undefined): number | null {
  const min = zoom?.min;

  if (typeof min !== "number" || !(min > 0) || min >= WIDE_ZOOM_BELOW) {
    return null;
  }

  return min;
}

/**
 * Ce que la page propose pour la caméra ouverte : son grand angle quand elle
 * zoome sous 1×, sinon les caméras du côté filmé quand il y en a plusieurs,
 * sinon rien.
 *
 * Une caméra dont le navigateur ne dit pas le côté reste dans la liste : mieux
 * vaut une caméra de trop qu'un grand angle introuvable. Celle qui filme y
 * reste toujours, pour que la liste montre ce qu'on voit.
 */
export function lensOffer(
  zoom: ZoomRange | undefined,
  cameras: readonly CameraDevice[],
  side: CameraFacing,
  activeDeviceId: string,
): LensOffer | null {
  const wide = wideZoomOf(zoom);

  if (wide !== null) {
    return { kind: "zoom", wide };
  }

  const lenses = cameras
    .filter((camera) => camera.deviceId === activeDeviceId || camera.facing.length === 0 || camera.facing.includes(side))
    .map((camera, index) => ({
      deviceId: camera.deviceId,
      label: camera.label === "" ? `Caméra ${index + 1}` : camera.label,
    }));

  return lenses.length >= 2 ? { kind: "devices", lenses } : null;
}

/**
 * Le grand angle est-il ce que la caméra filme ? Le zoom relu sur la piste peut
 * être arrondi : le plus proche des deux zooms proposés l'emporte.
 */
export function isWideZoom(zoom: number, wide: number): boolean {
  return zoom < (wide + 1) / 2;
}

/** Ce qu'un côté retient d'un zoom : rien à 1×, le zoom de toute caméra qu'on ouvre. */
export function zoomChoice(zoom: number): LensChoice | undefined {
  return zoom === 1 ? undefined : { kind: "zoom", zoom };
}

/**
 * Ce qu'un côté retient une fois sa caméra ouverte. Un objectif choisi qui ne
 * s'est pas ouvert a laissé la place à la caméra du côté, et c'est elle qu'on
 * retrouvera : sinon le champ changerait tout seul à la réouverture suivante.
 */
export function choiceAfterOpening(chosen: LensChoice | undefined, openedDeviceId: string | undefined): LensChoice | undefined {
  if (chosen?.kind !== "device" || openedDeviceId === undefined) {
    return chosen;
  }

  return openedDeviceId === chosen.deviceId ? chosen : undefined;
}

/** « 0,5× », « 1× » : un zoom comme l'écrit l'appareil photo, en français. */
export function zoomLabel(zoom: number): string {
  return `${zoom.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}×`;
}

/**
 * Ouvre la caméra d'un côté : l'objectif choisi pour ce côté s'il y en a un,
 * sinon celle que le navigateur préfère. Un objectif disparu ou refusé retombe
 * sur la seconde plutôt que de laisser le téléphone sans image.
 */
export async function openCameraStream(facing: CameraFacing, chosen: LensChoice | undefined): Promise<MediaStream> {
  if (chosen?.kind === "device") {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { ...CAMERA_CAPTURE, deviceId: { exact: chosen.deviceId } },
        audio: false,
      });
    } catch {
      // La caméra du côté prend sa place, ci-dessous.
    }
  }

  return navigator.mediaDevices.getUserMedia({
    video: { ...CAMERA_CAPTURE, facingMode: { ideal: facing } },
    audio: false,
  });
}

/** Les caméras de l'appareil. Avant la permission, le navigateur n'en donne ni noms ni identifiants. */
export async function listCameras(): Promise<CameraDevice[]> {
  const devices = await navigator.mediaDevices.enumerateDevices().catch(() => [] as MediaDeviceInfo[]);

  return devices
    .filter((device) => device.kind === "videoinput" && device.deviceId !== "")
    .map((device) => ({ deviceId: device.deviceId, label: device.label, facing: facingOf(device) }));
}

/** Le côté d'une caméra sans l'ouvrir, là où la liste du navigateur le donne (Chrome 67, Safari 17). */
function facingOf(device: MediaDeviceInfo): readonly string[] {
  const input = device as MediaDeviceInfo & { getCapabilities?: () => MediaTrackCapabilities };

  if (typeof input.getCapabilities !== "function") {
    return [];
  }

  try {
    return input.getCapabilities().facingMode ?? [];
  } catch {
    return [];
  }
}

/** L'étendue du zoom de la piste, quand le navigateur la donne. */
export function zoomRangeOf(track: MediaStreamTrack): ZoomRange | undefined {
  if (typeof track.getCapabilities !== "function") {
    return undefined;
  }

  try {
    return (track.getCapabilities() as MediaTrackCapabilities & { zoom?: ZoomRange }).zoom;
  } catch {
    return undefined;
  }
}

/** Le zoom que la piste dit filmer, quand elle en a un. */
export function zoomOf(track: MediaStreamTrack): number | null {
  const zoom = (track.getSettings() as MediaTrackSettings & { zoom?: number }).zoom;

  return typeof zoom === "number" ? zoom : null;
}

/**
 * Pose un zoom sur la piste. Selon la spécification, les contraintes appliquées
 * remplacent toutes les précédentes : celles de la capture repartent avec le
 * zoom, par prudence. WebKit, seul navigateur où ce zoom sert aujourd'hui,
 * garderait de lui-même sa définition et sa cadence.
 */
export function applyZoom(track: MediaStreamTrack, zoom: number): Promise<void> {
  return track.applyConstraints({ ...CAMERA_CAPTURE, zoom: { exact: zoom } } as MediaTrackConstraints);
}
